import { put, all, takeLatest, select } from "redux-saga/effects";
import * as actionTypes from "./actionTypes";
import * as extractionsActionTypes from "../extractions/actionTypes";
import API from "../../commonUtils/API.js";
import { toast } from "react-toastify";
import { handleApiError } from "../../commonUtils/Functions";
import { parseBackendErrors } from "../../commonUtils/queryBuilder.helper.js";
import { setQBErrors } from "../extractions/action";


function* getExtDataCategoriesSaga({ params }) {
    const { extId, keyword, versionId, is_draft } = params;
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        let queryString = '';
        if (keyword)              queryString += `&keyword=${encodeURIComponent(keyword)}`;
        if (versionId)            queryString += `&versionId=${versionId}`;
        if (is_draft !== undefined && is_draft !== null) queryString += `&is_draft=${is_draft}`;

        const { data } = yield API.get(`/ext/${extId}/data-categories?${queryString}`);
        if (data.success) {
            yield put({ type: actionTypes.GET_EXT_DATA_CATEGORIES_SUCCESS, payload: data.data });
        } else {
            toast.error("Oops! Something went unexpectedly wrong.");
            yield put({ type: actionTypes.GET_EXT_DATA_CATEGORIES_FAILURE, payload: data });
        }

    } catch (error) {
        handleApiError(error, "Oops! Something went unexpectedly wrong.");
        const data = error?.response?.data;
        yield put({ type: actionTypes.GET_EXT_DATA_CATEGORIES_FAILURE, payload: data || { message: "Internal server error" } });
    }
};

function* getExtDataCategorySaga({ id }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const extractionData = yield select((state) => state.Extractions.extractionData);

        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ 
                type: actionTypes.GET_EXT_DATA_CATEGORY_FAILURE, 
                payload: { message: "Extraction ID not found" } 
            });
            return;
        }

        const { data } = yield API.get(`/ext/${extractionData.id}/data-categories/${id}`);
        if (data.success) {
            yield put({ type: actionTypes.GET_EXT_DATA_CATEGORY_SUCCESS, payload: data.data });
        } else {
            toast.error("Could not load data category.");
            yield put({ type: actionTypes.GET_EXT_DATA_CATEGORY_FAILURE, payload: data });
        }

    } catch (error) {
        handleApiError(error, "Could not load data category.");
        const data = error?.response?.data;
        yield put({ type: actionTypes.GET_EXT_DATA_CATEGORY_FAILURE, payload: data || { message: "Internal server error" } });
    }
}

function* createExtDataCategorySaga({ payload }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });
        
        const { formData } = payload;
        const extractionData = yield select((state) => state.Extractions.extractionData);
        
        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ 
                type: actionTypes.CREATE_EXT_DATA_CATEGORY_FAILURE, 
                payload: { message: "Extraction ID not found" } 
            });
            return;
        }

        const { data } = yield API.post(`/ext/${extractionData.id}/data-categories`, formData);
        
        if (data.success) {
            yield put(setQBErrors({}));
            yield put({ 
                type: actionTypes.CREATE_EXT_DATA_CATEGORY_SUCCESS, 
                payload: data.data 
            });

            // Read filters from ExtDataCategories slice, not Extractions
            const filters = yield select((state) => state.ExtDataCategories.filters);
            yield put({ type: actionTypes.GET_EXT_DATA_CATEGORIES, params: { extId: extractionData?.id, ...filters } });
        } else {
            if (data?.data) {
                const parsedErrors = parseBackendErrors(data.data);
                yield put(setQBErrors(parsedErrors));
                toast.error("Please fix validation errors");
            } else {
                toast.error(data?.message || "Could not save data category.");
            }
            yield put({ 
                type: actionTypes.CREATE_EXT_DATA_CATEGORY_FAILURE, 
                payload: data 
            });
        }

    } catch (error) {
        const errorData = error?.response?.data;
        if (errorData?.data) {
            const parsedErrors = parseBackendErrors(errorData.data);
            yield put(setQBErrors(parsedErrors));
            toast.error("Please fix validation errors");
        } else {
            handleApiError(error, "Could not save data category.");
        }
        yield put({ 
            type: actionTypes.CREATE_EXT_DATA_CATEGORY_FAILURE, 
            payload: { 
                message: errorData?.message || "Internal server error",
                errors: errorData?.data || null
            } 
        });
    }
};

function* updateExtDataCategorySaga({ payload }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });
        
        const { id, formData } = payload;
        const extractionData = yield select((state) => state.Extractions.extractionData);

        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ 
                type: actionTypes.UPDATE_EXT_DATA_CATEGORY_FAILURE, 
                payload: { message: "Extraction ID not found" } 
            });
            return;
        }
        
        const { data } = yield API.put(`/ext/${extractionData.id}/data-categories/${id}`, formData);
        
        if (data.success) {
            yield put(setQBErrors({}));
            yield put({ 
                type: actionTypes.UPDATE_EXT_DATA_CATEGORY_SUCCESS, 
                payload: data.data 
            });

            // Read filters from ExtDataCategories slice, not Extractions
            const filters = yield select((state) => state.ExtDataCategories.filters);
            yield put({ type: actionTypes.GET_EXT_DATA_CATEGORIES, params: { extId: extractionData?.id, ...filters } });
        } else {
            if (data?.data) {
                const parsedErrors = parseBackendErrors(data.data);
                yield put(setQBErrors(parsedErrors));
                toast.error("Please fix validation errors");
            } else {
                toast.error(data?.message || "Could not save updated data category data.");
            }
            yield put({ 
                type: actionTypes.UPDATE_EXT_DATA_CATEGORY_FAILURE, 
                payload: data 
            });
        }

    } catch (error) {
        const errorData = error?.response?.data;
        if (errorData?.data) {
            const parsedErrors = parseBackendErrors(errorData.data);
            yield put(setQBErrors(parsedErrors));
            toast.error("Please fix validation errors");
        } else {
            handleApiError(error, "Could not save updated data category data.");
        }
        yield put({ 
            type: actionTypes.UPDATE_EXT_DATA_CATEGORY_FAILURE, 
            payload: { 
                message: errorData?.message || "Internal server error",
                errors: errorData?.data || null
            } 
        });
    }
};

function* updateExtDataCategoriesOrderSaga({ payload }) {
    const extractionData = yield select((state) => state.Extractions.extractionData);
    const filters = yield select((state) => state.ExtDataCategories.filters);
    try {
        const { orderData } = payload;

        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ 
                type: actionTypes.UPDATE_EXT_DATA_CATEGORIES_ORDER_FAILURE, 
                payload: { message: "Extraction ID not found" } 
            });
            return;
        }

        const { data } = yield API.put(
            `/ext/${extractionData.id}/data-categories/update-order`, 
            { orderData }
        );

        if (data.success) {
            yield put({ 
                type: actionTypes.UPDATE_EXT_DATA_CATEGORIES_ORDER_SUCCESS, 
                payload: data.data 
            });
        } else {
            toast.error(data?.message || "Could not update order.");
            yield put({ 
                type: actionTypes.UPDATE_EXT_DATA_CATEGORIES_ORDER_FAILURE, 
                payload: data 
            });
        }

    } catch (error) {
        handleApiError(error, "Could not update order.");
        const data = error?.response?.data;
        toast.error("Failed to update order. Refreshing...");
        yield put({ type: actionTypes.GET_EXT_DATA_CATEGORIES, params: { extId: extractionData?.id, ...filters } });
        yield put({ 
            type: actionTypes.UPDATE_EXT_DATA_CATEGORIES_ORDER_FAILURE, 
            payload: data || { message: "Internal server error" } 
        });
    }
};

function* deleteExtDataCategorySaga({ Id }) {
    try {
        const extractionData = yield select((state) => state.Extractions.extractionData);
        const filters = yield select((state) => state.ExtDataCategories.filters);
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const { data } = yield API.delete(`/ext/${extractionData.id}/data-categories/${Id}`);

        if (data.success) {
            yield put({ type: actionTypes.DELETE_EXT_DATA_CATEGORY_SUCCESS, payload: data });
            yield put({ type: actionTypes.GET_EXT_DATA_CATEGORIES, params: { extId: extractionData?.id, ...filters } });
        } else {
            toast.error(data?.message || "Could not delete data category.");
            yield put({ type: actionTypes.DELETE_EXT_DATA_CATEGORY_FAILURE, payload: data });
        }

    } catch (error) {
        handleApiError(error, "Could not delete data category.");
        const data = error?.response?.data;
        toast.error(data?.message || "Could not delete data category.");
        yield put({ type: actionTypes.DELETE_EXT_DATA_CATEGORY_FAILURE, payload: data || { message: "Internal server error" } });
    }
};

function* setOpenModalSaga({ payload }) {
    if (!payload) {
        yield put({ type: extractionsActionTypes.RESET_QUERY_BUILDER });
    }
}

function* ExtDataCategoriesSaga() {
  yield all([
    takeLatest(actionTypes.GET_EXT_DATA_CATEGORIES, getExtDataCategoriesSaga),
    takeLatest(actionTypes.GET_EXT_DATA_CATEGORY, getExtDataCategorySaga),
    takeLatest(actionTypes.CREATE_EXT_DATA_CATEGORY, createExtDataCategorySaga),
    takeLatest(actionTypes.UPDATE_EXT_DATA_CATEGORY, updateExtDataCategorySaga),
    takeLatest(actionTypes.UPDATE_EXT_DATA_CATEGORIES_ORDER, updateExtDataCategoriesOrderSaga),
    takeLatest(actionTypes.DELETE_EXT_DATA_CATEGORY, deleteExtDataCategorySaga),
    takeLatest(actionTypes.SET_OPEN_MODAL, setOpenModalSaga)
  ]);
}

export default ExtDataCategoriesSaga;